import { isServer, makeCopy } from "@/services/Helpers";
import {
  QuestHeroSelections,
  applyCombatStats,
  applyMissionCompleteState,
  buildHero,
  getDefaultHero,
  heroCompleteMission,
  heroGainInjury
} from "@/services/QuestService";
import { PayloadAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { shuffle, uniq } from "lodash-es";
import { STORAGE_KEYS } from "../globals";
import {
  AsyncStatus,
  GameSystems,
  IArmyData,
  IQuestClass,
  IQuestConsumable,
  IQuestInjury,
  IQuestSkill,
  ISelectedUnit,
  IUnit,
  IUpgradePackage,
  MissionMapItem,
  QuestGameType
} from "../types";
import { RootState } from "./store";

interface QuestState {
  skills: IQuestSkill[];
  consumables: IQuestConsumable[];
  injuries: IQuestInjury[];
  conditions: IQuestInjury[];
  classes: IQuestClass[];
  loadStatus: AsyncStatus;
  localHeroes: QuestHeroSelections[];
  localHeroesStatus: AsyncStatus;
  serverHeroes: QuestHeroSelections[];
  serverHeroesStatus: AsyncStatus;
  mission: QuestMissionState;
  heroData: IHeroData[];
  heroDataLoadingIds: string[];
  heroDataErrors: string[];
  newPlayerView: NewPlayerViews;
  newPlayerType: QuestGameType;
}

interface IHeroData {
  heroId: string;
  book: IArmyData;
  shop: IUpgradePackage[];
  heroCost: number;
  followerUnits: IUnit[];
  questCalcValues: Record<string, any>;
}

export enum NewPlayerViews {
  Start,
  Journey,
  Heroes,
  Collections,
  Skip
}

export enum QuestWaveType {
  Sentries,
  Reinforcements
}

export interface PlayerSocket {
  socketId: string;
  playerId: string;
}

export interface QuestMissionState {
  heroes: QuestHeroSelections[];
  difficulty: number;
  hordeMode: boolean;
  waves: QuestWave[];
  lastWave: QuestWave;
  waveNumber: number;
  enemiesKilled: number;
  collectionIds: string[];
  pages: MissionCompletePageState[];
  completionPageIndex: number;
  alertness: number;
  lobby: {
    playerId: string;
    id: string;
    host: PlayerSocket;
    clients: PlayerSocket[];
  };
  showCompletion: boolean;
  missionFailed: boolean;
  mapItemLocations: MissionMapItem[];
}

export interface MissionCompletePageState {
  heroId: string;
  index: number;
  step: MissionCompleteStep;
  state: MissionCompletionState;
  levelChoices: ILevelUpChoice[];
}

export enum MissionCompleteStep {
  Summary,
  Level,
  HighScore,
  Shop
}

export interface MissionCompletionState {
  //heroId: string;
  xpGain: number;
  goldGain: number;
  heroStatus: HeroStatus;
  heroInjury?: string;
  shopActions?: PayloadAction<any>[];
}

export enum HeroStatus {
  Survived,
  KilledInjury,
  KilledPerma
}

export interface QuestWave {
  id: string;
  type: QuestWaveType;
  units: ISelectedUnit[];
  cost: number;
}

export interface ILevelUpChoice {
  level: number;
  actions: PayloadAction<any>[];
}

const initialState: QuestState = {
  skills: [],
  consumables: [],
  injuries: [],
  conditions: [],
  classes: null,
  loadStatus: AsyncStatus.idle,
  localHeroes: null,
  localHeroesStatus: AsyncStatus.idle,
  serverHeroes: null,
  serverHeroesStatus: AsyncStatus.idle,
  mission: null,
  heroData: [],
  heroDataLoadingIds: [],
  heroDataErrors: [],
  newPlayerView: isServer()
    ? null
    : localStorage.getItem(STORAGE_KEYS.QUEST_SKIP_TUTORIAL) === "true"
      ? NewPlayerViews.Skip
      : null,
  newPlayerType: null
};

export const getQuestRules = createAsyncThunk("quest/getQuestRules", async (gameSystem: GameSystems) => {
  const res = await fetch("/api/quest/rules/" + gameSystem);
  const data = await res.json();
  return data;
});

export const getQuestHeroBookData = createAsyncThunk(
  "quest/getQuestHeroBookData",
  async (hero: QuestHeroSelections) => {
    const res = await fetch("/api/quest/hero-book-data", {
      method: "POST",
      body: JSON.stringify(hero)
    });
    const data = await res.json();
    return data;
  },
  {
    condition: (hero: QuestHeroSelections, { getState }) => {
      const rootState = getState() as RootState;
      return !rootState.quest.heroDataLoadingIds.includes(hero.id);
    }
  }
);

export const getLocalHeroes = createAsyncThunk("quest/getLocalHeroes", async () => {
  const saves: QuestHeroSelections[] = JSON.parse(
    localStorage[STORAGE_KEYS.QUEST_HEROES] || "[]"
  ) as QuestHeroSelections[];

  return saves;
});

export const getServerHeroes = createAsyncThunk("quest/getServerHeroes", async () => {
  const res = await fetch("/api/account/heroes");
  const data = await res.json();
  return data;
});

const getSavedHeroes = () =>
  JSON.parse(localStorage[STORAGE_KEYS.QUEST_HEROES] || "[]") as QuestHeroSelections[];
const setSavedHeroes = (saves) => (localStorage[STORAGE_KEYS.QUEST_HEROES] = JSON.stringify(saves));

export const QUEST_SAVE_VERSION = 2;

export const saveQuestHero = createAsyncThunk(
  "quest/saveQuestHero",
  async (hero: QuestHeroSelections, { getState }) => {
    const saveHero = { ...hero, saveVersion: QUEST_SAVE_VERSION };
    const saves = getSavedHeroes();
    const existingIndex = saves.findIndex((x) => x.id === saveHero.id);

    if (existingIndex >= 0) {
      saves.splice(existingIndex, 1, saveHero);
    } else {
      saves.push(saveHero);
    }

    const state = getState() as RootState;
    if (state.app.auth.loggedIn) {
      await fetch("/api/account/heroes/" + saveHero.id, {
        method: "POST",
        body: JSON.stringify(saveHero)
      });
    }

    setSavedHeroes(saves);

    return { saves, saveHero };
  }
);

export const deleteSavedHero = createAsyncThunk(
  "quest/deleteSavedHero",
  async (heroId: string, { getState }) => {
    const rootState = getState() as RootState;

    if (rootState.app.auth.loggedIn) {
      try {
        await fetch("/api/account/heroes/" + heroId, {
          method: "DELETE"
        });
      } catch (e) {
        console.error(e);
      }
    }

    const saves = getSavedHeroes();
    const existingIndex = saves.findIndex((x) => x.id === heroId);

    if (existingIndex >= 0) {
      saves.splice(existingIndex, 1);
    }
    setSavedHeroes(saves);
    return heroId;
  }
);

export const shareHero = createAsyncThunk("quest/shareHero", async (heroId: string, { getState }) => {
  const state = getState() as RootState;
  if (state.app.auth.loggedIn) {
    await fetch("/api/account/heroes/" + heroId + "/share", {
      method: "PUT"
    });
  }
  return heroId;
  //toggleHeroSha
  // const rootState = getState() as RootState;
  // const saves = getSavedHeroes();
  // const existingIndex = saves.findIndex((x) => x.id === heroId);

  // if (existingIndex >= 0) {
  //   saves[existingIndex].shared = !saves[existingIndex].shared;
  // }
  // setSavedHeroes(saves);
  // return heroId;
});

export const missionComplete = createAsyncThunk(
  "quest/missionComplete",
  async (payload: { save: boolean }, { getState, dispatch }) => {
    const { save } = payload;
    const rootState = getState() as RootState;
    const state = rootState.quest;
    const { missionFailed } = state.mission;
    const heroes = makeCopy(state.mission.heroes);
    const playerId = state.mission.lobby?.playerId;

    for (const page of state.mission.pages) {
      const completionState = page.state;
      const hero = heroes.find((x) => x.id === page.heroId);
      hero.actions.push(heroCompleteMission());

      if (!missionFailed) {
        applyMissionCompleteState(page, hero);
      }

      const displayHero = buildHero(
        hero,
        state.heroData.find((x) => x.heroId === hero.id),
        state.skills
      );

      if (completionState.heroStatus === HeroStatus.KilledInjury) {
        const injuries = state.injuries;
        const existingInjuries = displayHero.injuries.map((x) => x.name);
        const injuryId = completionState.heroInjury;
        const injury =
          injuryId && injuryId !== "-1"
            ? injuries.find((x) => x.id === injuryId)
            : shuffle(state.injuries.filter((i) => !existingInjuries.includes(i.name)))[0];
        hero.actions.push(heroGainInjury(injury.name));
      } else if (completionState.heroStatus === HeroStatus.KilledPerma) {
        // TODO: ...perma delete character ???
      }
    }

    if (save) {
      const saves = getSavedHeroes();
      // Either solo game, or only my heroes
      for (const x of heroes.filter((x) => !playerId || x.playerId === playerId)) {
        const hero = makeCopy(x);

        // Don't save the lobby Socket ID
        delete hero.playerId;

        //#region Tracking

        if (!hero.tracking) {
          hero.tracking = {
            gold: 0,
            stress: hero.end,
            wounds: 0,
            conditions: []
          };
        }

        hero.tracking.gold = 0;

        if (missionFailed) {
          hero.tracking.stress = hero.end;
          hero.tracking.wounds = 0;
          hero.tracking.conditions = [];
        } else {
          hero.tracking.stress = Math.min(hero.end, hero.tracking.stress + 3); // Recover 3 Power
          hero.tracking.wounds = Math.max(0, hero.tracking.wounds - 3);
        }

        //#endregion

        const heroWasPermaKilled =
          state.mission.pages.find((x) => x.heroId === hero.id).state.heroStatus === HeroStatus.KilledPerma;

        const existingIndex = saves.findIndex((x) => x.id === hero.id);
        if (existingIndex >= 0) {
          if (heroWasPermaKilled) {
            saves.splice(existingIndex, 1);
          } else {
            saves.splice(existingIndex, 1, hero);
          }
        } else {
          saves.push(hero);
        }

        dispatch(saveQuestHero(hero));

        //setSavedHeroes(saves);
      }
      return { heroes, saves };
    }

    return { heroes, saves: null };
  }
);

export const questLastMissionKey = "AF_QuestLastMission";

const saveMission = (mission) => {
  localStorage[questLastMissionKey] = JSON.stringify(mission);
};

const questSlice = createSlice({
  name: "quest",
  initialState,
  reducers: {
    setNewPlayerView(state, action: PayloadAction<NewPlayerViews>) {
      state.newPlayerView = action.payload;

      if (action.payload === NewPlayerViews.Skip) {
        localStorage.setItem(STORAGE_KEYS.QUEST_SKIP_TUTORIAL, "true");
      }
    },
    setNewPlayerType(state, action: PayloadAction<QuestGameType>) {
      state.newPlayerType = action.payload;
    },
    setQuestSkills(state, action) {
      state.skills = action.payload;
    },

    //#region Missions
    startMission(
      state,
      action: PayloadAction<{
        difficulty: number;
        hordeMode: boolean;
        heroes: QuestHeroSelections[];
        collectionIds: string[];
      }>
    ) {
      state.mission = {
        ...action.payload,
        heroes: makeCopy(action.payload.heroes),
        waves: [],
        lastWave: null,
        pages: [],
        completionPageIndex: 0,
        alertness: 0,
        waveNumber: 1,
        enemiesKilled: 0,
        lobby: {
          id: null,
          playerId: null,
          host: null,
          clients: []
        },
        showCompletion: false,
        missionFailed: false,
        mapItemLocations: []
      };

      // Initialise
      for (const hero of state.mission.heroes) {
        const statsApplied = applyCombatStats(getDefaultHero(hero));
        hero.tracking.gold = 0;
        hero.tracking.stress = statsApplied.end;
      }

      saveMission(state.mission);
    },
    setLobbyDetails(
      state,
      action: PayloadAction<{
        id: string;
        playerId: string;
        host: PlayerSocket;
        clients: PlayerSocket[];
      }>
    ) {
      state.mission.lobby = action.payload;

      saveMission(state.mission);
    },
    reloadMission(state) {
      const lastMission = localStorage[questLastMissionKey];
      if (lastMission) state.mission = JSON.parse(lastMission);
    },
    setMission(state, action: PayloadAction<QuestMissionState>) {
      state.mission = action.payload;
    },
    addWave(state, action: PayloadAction<QuestWave>) {
      state.mission.waves.push(action.payload);
      state.mission.lastWave = action.payload;
      state.mission.alertness = 0;
      //state.mission.waveNumber += 1;
      saveMission(state.mission);
    },
    removeEnemy(state, action: PayloadAction<{ waveId: string; unitId: string }>) {
      const { waveId, unitId } = action.payload;
      const wave = state.mission.waves.find((x) => x.id === waveId);
      wave.units.splice(
        wave.units.findIndex((x) => x.selectionId === unitId),
        1
      );
      state.mission.enemiesKilled++;
      saveMission(state.mission);
    },
    updateMissionHeroes(state, action: PayloadAction<QuestHeroSelections[]>) {
      state.mission.heroes = action.payload;
      saveMission(state.mission);
    },
    addMissionPlayer(
      state,
      action: PayloadAction<{
        playerSocket: PlayerSocket;
        heroes: QuestHeroSelections[];
      }>
    ) {
      const { playerSocket, heroes } = action.payload;

      const joiningHeroes = heroes.map((x) => ({
        ...x,
        playerId: playerSocket.playerId
      }));
      for (const hero of joiningHeroes) {
        const existing = state.mission.heroes.find((x) => x.id === hero.id);
        if (existing) {
          // Update playerid // Should no longer need this
          //existing.playerId = playerSocket.playerId;
        } else {
          state.mission.heroes.push(hero);
        }
      }

      // If the same player rejoins with a new socket id (e.g. page refresh), replace the old id.
      const existingClient = state.mission.lobby.clients.find((x) => x.playerId === playerSocket.playerId);
      if (existingClient) {
        existingClient.socketId = playerSocket.socketId;
      } else {
        state.mission.lobby.clients.push(playerSocket);
      }
      saveMission(state.mission);
    },
    lobbyHostReconnected(state, action: PayloadAction<{ hostSocketId: string }>) {
      state.mission.lobby.host.socketId = action.payload.hostSocketId;
      saveMission(state.mission);
    },
    lobbyPlayerReconnected(state, action: PayloadAction<PlayerSocket>) {
      const existingClient = state.mission.lobby.clients.find((x) => x.playerId === action.payload.playerId);
      if (existingClient) {
        existingClient.socketId = action.payload.socketId;
      } else {
        state.mission.lobby.clients.push(action.payload);
      }
      saveMission(state.mission);
    },
    lobbyPlayerDisconnected(state, action: PayloadAction<string>) {
      const clientId = action.payload;
      state.mission.lobby.clients = state.mission.lobby.clients.filter((x) => x.socketId !== clientId);
      saveMission(state.mission);
    },
    addMissionHeroEvent(state, action: PayloadAction<{ heroId: string; action: PayloadAction<any> }>) {
      const { heroId, action: heroAction } = action.payload;
      const hero = state.mission.heroes.find((x) => x.id === heroId);
      (hero.actions ?? (hero.actions = [])).push(heroAction);

      saveMission(state.mission);
    },
    adjustMissionHeroTracking(
      state,
      action: PayloadAction<{
        heroId: string;
        key: string;
        amt?: number;
        condition: boolean;
      }>
    ) {
      const { heroId, key, amt, condition } = action.payload;
      const hero = state.mission.heroes.find((x) => x.id === heroId);
      if (!hero.tracking) {
        hero.tracking = {
          gold: 0,
          stress: 0,
          wounds: 0,
          conditions: []
        };
      }

      if (condition) {
        const existingIndex = (hero.tracking.conditions || []).findIndex((x) => x === key);
        if (amt === 0 && existingIndex >= 0) {
          hero.tracking.conditions.splice(existingIndex, 1);
        } else if (amt === 1) {
          (hero.tracking.conditions ?? (hero.tracking.conditions = [])).push(key);
        }
      } else {
        hero.tracking[key] += amt;
      }

      saveMission(state.mission);
    },
    setMissionAlertness(state, action: PayloadAction<number>) {
      state.mission.alertness = action.payload;
      saveMission(state.mission);
    },
    setMissionCompletePages(state, action: PayloadAction<MissionCompletePageState[]>) {
      state.mission.pages = action.payload;
      state.mission.completionPageIndex = 0;
    },
    setMissionCompletePage(state, action: PayloadAction<MissionCompletePageState>) {
      const page = action.payload;

      state.mission.pages = (state.mission.pages ?? []).filter((x) => x.heroId !== page.heroId).concat(page);
    },
    setMissionCompletePageIndex(state, action: PayloadAction<number>) {
      state.mission.completionPageIndex = action.payload;
    },
    startEditHeroMission(state, action: PayloadAction<{ hero: QuestHeroSelections }>) {
      state.mission = {
        heroes: [action.payload.hero],
        pages: [],
        completionPageIndex: 0,
        difficulty: 1,
        hordeMode: false,
        enemiesKilled: 0,
        alertness: 0,
        collectionIds: [],
        waves: [],
        lastWave: null,
        waveNumber: 1,
        lobby: { host: null, playerId: null, id: null, clients: [] },
        showCompletion: false,
        missionFailed: false,
        mapItemLocations: []
      };
    },
    setShowCompletion(state, action: PayloadAction<{ showCompletion: boolean; missionFailed: boolean }>) {
      state.mission.showCompletion = action.payload.showCompletion;
      state.mission.missionFailed = action.payload.missionFailed;
    },
    applyHeroWaveComplete(state, action: PayloadAction<MissionCompletePageState>) {
      const heroes = state.mission.heroes;
      const page = action.payload;
      const hero = heroes.find((x) => x.id === page.heroId);
      if (hero) {
        // High Score Screen/Page has no hero
        applyMissionCompleteState(page, hero);
      }

      saveMission(state.mission);
    },
    hordeWaveComplete(state) {
      if (!state.mission.missionFailed) {
        // Apply choices directly to heroes in the mission
        // const heroes = state.mission.heroes;
        // for (const page of state.mission.pages) {
        //   const hero = heroes.find((x) => x.id === page.heroId);
        //   if (hero) {
        //     // High Score Screen/Page has no hero
        //     applyMissionCompleteState(page, hero);
        //   }
        // }

        // Go back
        state.mission.pages = [];
        state.mission.showCompletion = false;
        state.mission.waveNumber += 1;
      } else {
        // Display fail screen?
      }
      saveMission(state.mission);
    },

    addMapItem(state, action: PayloadAction<MissionMapItem>) {
      if (!state.mission.mapItemLocations) {
        state.mission.mapItemLocations = [];
      }
      state.mission.mapItemLocations.push(action.payload);
    }

    //#endregion
  },
  selectors: {
    selectQuestSkills(state) {
      return state.skills;
    },
    selectQuestInjuries(state) {
      return state.injuries;
    },
    selectQuestConsumables(state) {
      return state.consumables;
    },
    selectHeroData(state) {
      return state.heroData ?? [];
    },
    selectQuestBookRules(state) {
      return state.heroData.flatMap((x) => x.book?.specialRules ?? []);
    },
    selectLocalHeroes(state) {
      return state.localHeroes;
    },
    selectServerHeroes(state) {
      return state.serverHeroes;
    }
  },
  extraReducers(builder) {
    builder.addCase(getLocalHeroes.fulfilled, (state, action) => {
      state.localHeroes = action.payload.map((hero) => repairHeroClass(hero, state.classes));
      state.localHeroesStatus = AsyncStatus.succeeded;
    });
    builder
      .addCase(getServerHeroes.pending, (state) => {
        return { ...state, serverHeroesStatus: AsyncStatus.loading };
      })
      .addCase(getServerHeroes.rejected, (state) => {
        return { ...state, serverHeroesStatus: AsyncStatus.failed };
      })
      .addCase(getServerHeroes.fulfilled, (state, action) => {
        return {
          ...state,
          serverHeroes: action.payload.map((hero) => ({
            ...repairHeroClass(hero, state.classes),
            cloud: true
          })),
          serverHeroesStatus: AsyncStatus.succeeded
        };
      });
    builder.addCase(saveQuestHero.fulfilled, (state, action) => {
      const { saves, saveHero } = action.payload;
      state.localHeroes = saves;
      if (state.serverHeroesStatus === AsyncStatus.succeeded) {
        const existingIndex = state.serverHeroes.findIndex((x) => x.id === saveHero.id);
        if (existingIndex >= 0) {
          state.serverHeroes.splice(existingIndex, 1, saveHero);
        } else {
          state.serverHeroes.push(saveHero);
        }
      }
    });
    builder
      .addCase(getQuestRules.pending, (state) => {
        return {
          ...state,
          loadStatus: AsyncStatus.loading
        };
      })
      .addCase(getQuestRules.fulfilled, (state, action) => {
        const { skills, consumables, injuries, conditions, classes } = action.payload;
        return {
          ...state,
          skills,
          consumables,
          injuries,
          conditions,
          classes,
          loadStatus: AsyncStatus.succeeded
        };
      });
    builder
      .addCase(getQuestHeroBookData.fulfilled, (state, action) => {
        const heroId = action.meta.arg.id;
        state.heroDataLoadingIds = state.heroDataLoadingIds.filter((x) => x !== heroId);
        state.heroData = state.heroData
          .filter((x) => x.heroId !== action.payload.heroId)
          .concat(action.payload);
      })
      .addCase(getQuestHeroBookData.pending, (state, action) => {
        const heroId = action.meta.arg.id;
        if (!state.heroDataLoadingIds.includes(heroId)) {
          state.heroDataLoadingIds.push(heroId);
        }
      })
      .addCase(getQuestHeroBookData.rejected, (state, action) => {
        const heroId = action.meta.arg.id;
        state.heroDataLoadingIds = state.heroDataLoadingIds.filter((x) => x !== heroId);
        state.heroDataErrors = (state.heroDataErrors ?? []).concat(heroId);
      });
    builder.addCase(missionComplete.fulfilled, (state) => {
      state.mission = null;
      state.newPlayerType = null;
      state.newPlayerView = null;
    });
    builder.addCase(deleteSavedHero.fulfilled, (state, action) => {
      const heroId = action.payload;
      state.localHeroes = state.localHeroes.filter((x) => x.id !== heroId);
      state.serverHeroes = state.serverHeroes.filter((x) => x.id !== heroId);
    });
  }
});

const repairHeroClass = (hero: QuestHeroSelections, classes: IQuestClass[]): QuestHeroSelections => {
  const selections = hero.selections;
  if (typeof (selections.class as any) === "string") {
    const classDef = classes?.find((x) => x.name === (selections.class as any));
    if (!classDef) {
      console.error(`${selections.class} class not found`);
      return hero;
    }
    return {
      ...hero,
      selections: {
        ...selections,
        class: {
          id: classDef.id,
          name: classDef.name
        }
      }
    };
  }
  return hero;
};

export const {
  setNewPlayerView,
  setNewPlayerType,

  startMission,
  addWave,
  removeEnemy,
  addMissionHeroEvent,
  reloadMission,
  setMission,
  setMissionCompletePage,
  setMissionCompletePages,
  setMissionCompletePageIndex,
  startEditHeroMission,
  addMissionPlayer,
  setLobbyDetails,
  lobbyPlayerDisconnected,
  adjustMissionHeroTracking,
  setMissionAlertness,
  setShowCompletion,
  updateMissionHeroes,
  lobbyHostReconnected,
  lobbyPlayerReconnected,
  applyHeroWaveComplete,
  hordeWaveComplete,
  addMapItem
} = questSlice.actions;

export const { selectQuestSkills, selectQuestConsumables, selectQuestInjuries } = questSlice.selectors;

const { selectHeroData, selectLocalHeroes, selectServerHeroes } = questSlice.selectors;

export const selectQuestBookRules = createSelector([selectHeroData], (heroData) => {
  return heroData.flatMap((x) => (x.book?.specialRules ?? []).concat((x.book as any).customRules ?? []));
});

export const selectHeroDatum = createSelector(
  [selectHeroData, (state, heroId) => heroId],
  (heroData, heroId) => {
    return heroData.find((x) => x.heroId === heroId);
  }
);

export const selectAllHeroes = createSelector(
  [selectLocalHeroes, selectServerHeroes],
  (localHeroes, serverHeroes) => {
    const ids = uniq((localHeroes ?? []).map((x) => x.id).concat((serverHeroes ?? []).map((x) => x.id)));
    return ids.map((x) => {
      const server = serverHeroes?.find((s) => s.id === x);
      const local = localHeroes.find((l) => l.id === x);
      return server ?? local;
    });
  }
);

export default questSlice.reducer;
